import axios from 'axios'
import type {
  BlobStorageRequest, LogicAppsRequest, FunctionsRequest,
  AppServiceRequest, BandwidthRequest, CombinedRequest,
  CalculationResponse, CombinedResponse,
  PricesLastUpdatedResponse, ChatWorkerResponse,
  LogAnalyticsIngestionRequest, LogAnalyticsIngestionResponse,
  LogAnalyticsRetentionRequest, LogAnalyticsRetentionResponse,
  LogAnalyticsSearchJobRequest, LogAnalyticsSearchJobResponse,
  LogAnalyticsRestoreRequest, LogAnalyticsRestoreResponse,
  NetworkingRequest,
  FirewallRequest,
  FrontDoorRequest,
  FirewallResponse,
  FrontDoorResponse,
  DataFactoryRequest,
  DataFactoryResponse,
  VirtualDesktopRequest,
  VirtualDesktopResponse,
  SQLDatabaseRequest,
  SQLDatabaseResponse,
  ManagedDisksRequest,
  ManagedDisksResponse,
  OpenAIRequest,
  KeyVaultRequest,
  KeyVaultResponse,
  OpenAIResponse,
  AzureDevOpsRequest,
  AzureDevOpsResponse,
  SiteRecoveryRequest,
  SiteRecoveryResponse,
  AksRequest,
  AksResponse,
  SqlMiRequest,
  SqlMiResponse,
  AppGatewayRequest,
  AppGatewayResponse,
  VpnGatewayRequest,
  VpnGatewayResponse,
  BastionRequest,
  BastionResponse,
  AzureBackupRequest,
  AzureBackupResponse,
} from './types'

const apiBase = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5000'
const workerBase = import.meta.env.VITE_WORKER_URL ?? 'http://localhost:8787'

const api = axios.create({
  baseURL: apiBase,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

// Error handler — never expose internal details to UI
function handleError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment.')
    }
    if (error.response?.status === 400) {
      const msg = error.response.data?.error ?? 'Invalid input'
      throw new Error(msg)
    }
    if (!error.response) {
      throw new Error('Cannot reach the pricing service. Check your connection.')
    }
  }
  throw new Error('An unexpected error occurred.')
}

export async function calculateBlobStorage(
  inputs: BlobStorageRequest
): Promise<CalculationResponse> {
  try {
    const { data } = await api.post('/api/calculate/blob-storage', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateLogicApps(
  inputs: LogicAppsRequest
): Promise<CalculationResponse> {
  try {
    const { data } = await api.post('/api/calculate/logic-apps', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateFunctions(
  inputs: FunctionsRequest
): Promise<CalculationResponse> {
  try {
    const { data } = await api.post('/api/calculate/functions', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateAppService(
  inputs: AppServiceRequest
): Promise<CalculationResponse> {
  try {
    const { data } = await api.post('/api/calculate/app-service', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateBandwidth(
  inputs: BandwidthRequest
): Promise<CalculationResponse> {
  try {
    const { data } = await api.post('/api/calculate/bandwidth', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateCombined(
  inputs: CombinedRequest
): Promise<CombinedResponse> {
  try {
    const { data } = await api.post('/api/calculate/combined', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateLogAnalyticsIngestion(
  inputs: LogAnalyticsIngestionRequest
): Promise<LogAnalyticsIngestionResponse> {
  try {
    const { data } = await api.post('/api/calculate/log-analytics/ingestion', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateLogAnalyticsRetention(
  inputs: LogAnalyticsRetentionRequest
): Promise<LogAnalyticsRetentionResponse> {
  try {
    const { data } = await api.post('/api/calculate/log-analytics/retention', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateLogAnalyticsSearchJob(
  inputs: LogAnalyticsSearchJobRequest
): Promise<LogAnalyticsSearchJobResponse> {
  try {
    const { data } = await api.post('/api/calculate/log-analytics/search-job', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateLogAnalyticsRestore(
  inputs: LogAnalyticsRestoreRequest
): Promise<LogAnalyticsRestoreResponse> {
  try {
    const { data } = await api.post('/api/calculate/log-analytics/restore', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateNetworking(
  inputs: NetworkingRequest
): Promise<CalculationResponse> {
  try {
    const { data } = await api.post('/api/calculate/networking', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateFirewall(
  inputs: FirewallRequest
): Promise<FirewallResponse> {
  try {
    const { data } = await api.post('/api/calculate/firewall', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateFrontDoor(
  inputs: FrontDoorRequest
): Promise<FrontDoorResponse> {
  try {
    const { data } = await api.post('/api/calculate/front-door', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateDataFactory(
  inputs: DataFactoryRequest
): Promise<DataFactoryResponse> {
  try {
    const { data } = await api.post('/api/calculate/data-factory', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateVirtualDesktop(
  inputs: VirtualDesktopRequest
): Promise<VirtualDesktopResponse> {
  try {
    const { data } = await api.post('/api/calculate/virtual-desktop', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateSQLDatabase(
  inputs: SQLDatabaseRequest
): Promise<SQLDatabaseResponse> {
  try {
    const { data } = await api.post('/api/calculate/sql-database', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateManagedDisks(
  inputs: ManagedDisksRequest
): Promise<ManagedDisksResponse> {
  try {
    const { data } = await api.post('/api/calculate/managed-disks', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateOpenAI(
  inputs: OpenAIRequest
): Promise<OpenAIResponse> {
  try {
    const { data } = await api.post('/api/calculate/openai', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function getPricesLastUpdated(): Promise<PricesLastUpdatedResponse> {
  try {
    const { data } = await api.get('/api/prices/last-updated')
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateKeyVault(
  inputs: KeyVaultRequest
): Promise<KeyVaultResponse> {
  try {
    const { data } = await api.post('/api/calculate/key-vault', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateAzureDevOps(
  inputs: AzureDevOpsRequest
): Promise<AzureDevOpsResponse> {
  try {
    const { data } = await api.post('/api/calculate/azure-devops', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateSiteRecovery(
  inputs: SiteRecoveryRequest
): Promise<SiteRecoveryResponse> {
  try {
    const { data } = await api.post('/api/calculate/site-recovery', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateAks(
  inputs: AksRequest
): Promise<AksResponse> {
  try {
    const { data } = await api.post('/api/calculate/aks', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateSqlMi(
  inputs: SqlMiRequest
): Promise<SqlMiResponse> {
  try {
    const { data } = await api.post('/api/calculate/sql-managed-instance', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateAppGateway(
  inputs: AppGatewayRequest
): Promise<AppGatewayResponse> {
  try {
    const { data } = await api.post('/api/calculate/application-gateway', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateVpnGateway(
  inputs: VpnGatewayRequest
): Promise<VpnGatewayResponse> {
  try {
    const { data } = await api.post('/api/calculate/vpn-gateway', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateAzureBackup(
  inputs: AzureBackupRequest
): Promise<AzureBackupResponse> {
  try {
    const { data } = await api.post('/api/calculate/azure-backup', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function calculateBastion(
  inputs: BastionRequest
): Promise<BastionResponse> {
  try {
    const { data } = await api.post('/api/calculate/bastion', inputs)
    return data
  } catch (e) { return handleError(e) }
}

export async function submitChatQuery(
  message: string
): Promise<ChatWorkerResponse> {
  try {
    const { data } = await axios.post(
      `${workerBase}/chat`,
      { message },
      {
        timeout: 20000,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return data
  } catch (e) { return handleError(e) }
}

export async function submitChatAdjust(
  message: string,
  previousState: Record<string, unknown>
): Promise<ChatWorkerResponse> {
  try {
    const { data } = await axios.post(
      `${workerBase}/chat/adjust`,
      { message, previous_state: previousState },
      {
        timeout: 20000,
        headers: { 'Content-Type': 'application/json' },
      }
    )
    return data
  } catch (e) { return handleError(e) }
}
